import {
  Button,
  buttonVariants,
  Heading,
  Paragraph,
  Popover,
  PopoverContent,
  PopoverLink,
  PopoverTrigger,
} from '@/components/atoms';
import { cn } from '@/lib/utils';
import { EllipsisVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency } from '@/lib/helpers';
import type { MouseEventHandler } from 'react';
import { useState } from 'react';

interface ProductCardProps {
  id: number;
  images?: string[];
  image?: string;
  name: string;
  description: string;
  price: number;
  className?: string;
  quantity?: number;
  categoryName?: string;
  subcategoryName?: string;
  handleView?: () => void;
  handleEdit?: () => void;
  handleClick?: () => void;
  handleDelete?: MouseEventHandler<HTMLAnchorElement>;
  handleOOS?: MouseEventHandler<HTMLAnchorElement>;
  handleBIS?: MouseEventHandler<HTMLAnchorElement>;
  variant?: 'owner' | 'customer';
  storeId?: number;
}

export const ProductCard = ({
  images,
  image,
  name,
  description,
  price,
  quantity,
  categoryName,
  subcategoryName,
  className,
  handleView,
  handleEdit,
  handleClick,
  handleDelete,
  handleOOS,
  handleBIS,
  variant = 'customer',
}: ProductCardProps) => {
  const imageList = images && images.length > 0 ? images : image ? [image] : [];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? imageList.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === imageList.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div
      className={cn(
        'p-2 rounded-md border border-accent flex flex-col gap-1.5 transition-colors group bg-white max-w-md relative',
        className
      )}
    >
      {quantity === 0 && (
        <div className="z-20 absolute top-2 right-2 bg-amber-100 text-amber-700 p-2 py-1 rounded-tr-md rounded-bl-md text-xs">
          out of stock
        </div>
      )}
      <div className="flex flex-col gap-4 overflow-hidden">
        <div className="border border-accent bg-accent/40 rounded-sm h-52 p-6 flex items-center justify-center overflow-hidden relative group/carousel">
          {imageList.length > 0 && (
            <>
              <img
                src={imageList[currentImageIndex]}
                alt=""
                className="h-full object-contain transition-all duration-300"
                key={currentImageIndex}
              />
              {imageList.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-200"
                    aria-label="Next image"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col gap-1.5 justify-between px-2">
          <Heading>{name}</Heading>
          <Paragraph size="sm">{description}</Paragraph>
          {(categoryName || subcategoryName) && (
            <Paragraph size="sm" className="text-muted-foreground">
              {[categoryName, subcategoryName].filter(Boolean).join(' · ')}
            </Paragraph>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4 mt-auto p-2">
        <Paragraph size="lg" className="font-semibold text-primary mt-auto">
          {formatCurrency(price)}
        </Paragraph>
        {variant === 'customer' && (
          <Button
            label="Buy now"
            size="wide"
            className="rounded-sm"
            onClick={handleClick ?? handleView}
          />
        )}
        {variant === 'owner' && (
          <div className="grid grid-cols-12 gap-2">
            <Button
              label="View product"
              size="wide"
              className="rounded-sm col-span-10"
              onClick={handleView ?? handleClick}
            />
            <div className="col-span-2">
              <Popover>
                <PopoverTrigger
                  className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'w-full h-full'
                  )}
                >
                  <EllipsisVertical />
                </PopoverTrigger>
                <PopoverContent className="flex flex-col p-0 w-fit" align="end">
                  {handleEdit && (
                    <PopoverLink
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleEdit();
                      }}
                      label="Edit"
                    />
                  )}
                  {quantity! > 0 ? (
                    <PopoverLink
                      to="#"
                      onClick={handleOOS}
                      label="Out of stock"
                    />
                  ) : (
                    <PopoverLink
                      to="#"
                      onClick={handleBIS}
                      label="Back in stock"
                    />
                  )}
                  <PopoverLink
                    to="#"
                    onClick={handleDelete}
                    label="Delete"
                    className="text-destructive"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
